<template>
  <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''" v-if="site">
    <!-- HALLINTO/TIIMINVETÄJÄ: Listaus + luontilomake -->
    <template v-if="$isTenantAdmin || $isTenantManager">
      <v-card-title>
        {{ $t('sites.introduction.title') }}
      </v-card-title>
      <v-card-text>
        <v-data-table
          :items="introductions"
          :headers="headers"
          :loading="loading"
          :no-data-text="$t('sites.introduction.no_introductions')"
          :items-per-page="-1"
          hide-default-footer
        >
          <template v-slot:item.name="{ item }">
            {{ getWorkerName(item) }}
          </template>
          <template v-slot:item.accepted="{ item }">
            {{ formatDate(item.accepted) }}
          </template>
          <template v-slot:item.createdByUser="{ item }">
            {{ getFullName(item.createdByUser) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="error" @click="confirmDelete(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-btn color="primary" @click="showCreateDialog = true">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('sites.introduction.add_new') }}
        </v-btn>
      </v-card-actions>

      <!-- Poistovahvistus -->
      <v-dialog v-model="showDeleteDialog" max-width="400">
        <v-card>
          <v-card-title>
            {{ $t('sites.introduction.delete_confirm_title') }}
          </v-card-title>
          <v-card-text>
            {{ $t('sites.introduction.delete_confirm_message') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showDeleteDialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="error" @click="deleteIntroduction" :loading="deleting">
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Luontidialogi -->
      <v-dialog v-model="showCreateDialog" max-width="800">
        <v-card>
          <v-card-title>
            {{ $t('sites.introduction.add_new') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="showCreateDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <!-- Työntekijän valinta -->
            <v-autocomplete
              v-model="selectedWorkers"
              :items="workers"
              :label="$t('sites.introduction.select_workers')"
              item-text="fullName"
              item-value="userId"
              multiple
              chips
              deletable-chips
              small-chips
              clearable
              :loading="loadingWorkers"
              :search-input.sync="workerSearchText"
              @change="workerSearchText = ''"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.fullName }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <!-- Manuaalinen syöttö -->
            <v-combobox
              v-model="manualNames"
              :label="$t('sites.introduction.manual_names')"
              multiple
              chips
              deletable-chips
              small-chips
              clearable
              append-icon=""
            ></v-combobox>

            <v-divider class="my-4"></v-divider>

            <!-- Perehdytyskohdat -->
            <div class="d-flex align-center mb-2">
              <span class="subtitle-1">{{ $t('sites.introduction.steps') }}</span>
              <v-spacer></v-spacer>
              <v-btn small text color="primary" @click="selectAllSteps">
                {{ $t('sites.introduction.select_all_steps') }}
              </v-btn>
            </div>

            <v-list dense>
              <template v-for="step in introductionSteps">
                <v-divider class="mx-4" :key="`${step}_divider`"></v-divider>
                <v-list-item :key="step" @click="toggleStep(step)">
                  <v-list-item-content>
                    <v-list-item-title class="text-wrap body-2">
                      {{ $t(`sites.introduction.step.${step}`) }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn outlined icon color="primary">
                      <v-icon v-if="newIntroduction[step]">mdi-check-bold</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>

            <v-alert
              v-if="hasWorkers && !allStepsCompleted"
              type="warning"
              text
              dense
              class="mt-4"
            >
              {{ $t('sites.introduction.all_steps_required') }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeCreateDialog">{{ $t('cancel') }}</v-btn>
            <v-btn
              color="primary"
              @click="createBulkIntroductions"
              :loading="saving"
              :disabled="!canSave"
            >
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- PELKKÄ TYÖNTEKIJÄ: Read-only näkymä omasta perehdytyksestä -->
    <template v-else>
      <template v-if="myIntroduction">
        <v-card-title>
          {{ $t('sites.introduction.title') }}
        </v-card-title>
        <v-card-text>
          <v-alert type="info" text class="mb-4">
            {{ $t('sites.introduction.introduction_accepted') }}
          </v-alert>

          <div class="mb-2">
            <strong>{{ $t('sites.introduction.oriented_date') }}:</strong> {{ formatDate(myIntroduction.accepted) }}
          </div>
          <div class="mb-4" v-if="myIntroduction.createdByUser">
            <strong>{{ $t('sites.introduction.oriented_by') }}:</strong> {{ getFullName(myIntroduction.createdByUser) }}
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="subtitle-1 mb-2">{{ $t('sites.introduction.steps') }}</div>
          <v-list dense>
            <template v-for="step in introductionSteps">
              <v-list-item :key="step">
                <v-list-item-icon>
                  <v-icon v-if="myIntroduction[step]" color="success">mdi-check-circle</v-icon>
                  <v-icon v-else color="grey">mdi-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap body-2">
                    {{ $t(`sites.introduction.step.${step}`) }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card-text>
      </template>
      <template v-else>
        <v-card-text>
          <v-alert type="info" text class="mb-0">
            {{ $t('sites.introduction.no_orientation') }}
          </v-alert>
        </v-card-text>
      </template>
    </template>
  </v-card>
</template>

<script>
import introductionApi from "@/api/introduction";
import workerApi from "@/api/worker";
import dayjs from "dayjs";

export default {
  name: "SiteIntroduction",
  props: ['site'],
  data() {
    return {
      loading: false,
      loadingWorkers: false,
      saving: false,
      deleting: false,
      showCreateDialog: false,
      showDeleteDialog: false,
      introductionToDelete: null,
      introductions: [],
      workers: [],
      selectedWorkers: [],
      manualNames: [],
      workerSearchText: '',
      myIntroduction: null,
      newIntroduction: {
        yleistiedot: null,
        toteutusorganisaatio: null,
        tyomaatilat: null,
        tyosuojeluorganisaatio: null,
        paloturvallisuus: null,
        tyoterveyshuoltojaensiapu: null,
        liikenneturvallisuus: null,
        tyomaanjarjestysjasiisteys: null,
        tilaajanturvallisuusaineisto: null,
        tyomaankunnossapitotarkastukset: null,
        tyomaanvaaratekijat: null,
        erityisetvaaranpaikat: null,
        telineet: null,
        koneet: null,
        tyomaakierros: null,
      },
      introductionSteps: [
        'yleistiedot', 'toteutusorganisaatio', 'tyomaatilat', 'tyosuojeluorganisaatio', 'paloturvallisuus',
        'tyoterveyshuoltojaensiapu', 'liikenneturvallisuus', 'tyomaanjarjestysjasiisteys', 'tilaajanturvallisuusaineisto',
        'tyomaankunnossapitotarkastukset', 'tyomaanvaaratekijat', 'erityisetvaaranpaikat', 'telineet', 'koneet', 'tyomaakierros'
      ],
    }
  },
  computed: {
    headers() {
      return [
        { text: this.$t('sites.introduction.worker_name'), value: 'name' },
        { text: this.$t('sites.introduction.oriented_date'), value: 'accepted' },
        { text: this.$t('sites.introduction.oriented_by'), value: 'createdByUser' },
        { text: '', value: 'actions', sortable: false, width: '50px' },
      ];
    },
    hasWorkers() {
      return this.selectedWorkers.length > 0 || this.manualNames.length > 0;
    },
    allStepsCompleted() {
      return this.introductionSteps.every(step => this.newIntroduction[step] !== null);
    },
    canSave() {
      return this.hasWorkers && this.allStepsCompleted;
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('DD.MM.YYYY');
    },
    getFullName(user) {
      if (!user) return '-';
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      const fullName = (firstName + ' ' + lastName).trim();
      return fullName || '-';
    },
    getWorkerName(introduction) {
      if (introduction.user) {
        return this.getFullName(introduction.user);
      }
      if (introduction.manualName) {
        return introduction.manualName;
      }
      return '-';
    },
    toggleStep(step) {
      if (this.newIntroduction[step]) {
        this.newIntroduction[step] = null;
      } else {
        this.newIntroduction[step] = dayjs().toISOString();
      }
    },
    selectAllSteps() {
      const now = dayjs().toISOString();
      this.introductionSteps.forEach(step => {
        this.newIntroduction[step] = now;
      });
    },
    resetNewIntroduction() {
      this.introductionSteps.forEach(step => {
        this.newIntroduction[step] = null;
      });
      this.selectedWorkers = [];
      this.manualNames = [];
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.resetNewIntroduction();
    },
    async loadIntroductions() {
      if (!this.site?.id) return;
      this.loading = true;
      try {
        this.introductions = await introductionApi.getIntroductionsBySite(this.site.id);
      } catch (e) {
        this.$handleApiError(e);
      }
      this.loading = false;
    },
    async loadMyIntroduction() {
      if (!this.site?.id) return;
      this.loading = true;
      try {
        this.myIntroduction = await introductionApi.getIntroductionByUserAndSite(this.site.id);
      } catch (e) {
        this.$handleApiError(e);
      }
      this.loading = false;
    },
    async loadWorkers() {
      this.loadingWorkers = true;
      try {
        const workers = await workerApi.getWorkers();
        const mapped = workers
          .filter(w => w.person && !w.hidden)
          .map(w => ({
            ...w,
            fullName: w.person ? ((w.person.firstname || '') + ' ' + (w.person.lastname || '')).trim() : ''
          }))
          .filter(w => w.fullName);

        // Deduplikoi userId:n perusteella
        const seen = new Set();
        this.workers = mapped.filter(w => {
          if (seen.has(w.userId)) return false;
          seen.add(w.userId);
          return true;
        });
      } catch (e) {
        this.$handleApiError(e);
      }
      this.loadingWorkers = false;
    },
    async createBulkIntroductions() {
      if (!this.canSave) return;

      this.saving = true;
      try {
        const workers = [];

        // Add selected workers (by userId)
        this.selectedWorkers.forEach(userId => {
          workers.push({ userId: userId, manualName: null });
        });

        // Add manual names
        this.manualNames.forEach(name => {
          if (name && name.trim()) {
            workers.push({ userId: null, manualName: name.trim() });
          }
        });

        const request = {
          siteId: this.site.id,
          workers: workers,
          introductionData: { ...this.newIntroduction }
        };

        await introductionApi.createBulkIntroductions(request);
        this.closeCreateDialog();
        await this.loadIntroductions();
      } catch (e) {
        this.$handleApiError(e);
      }
      this.saving = false;
    },
    confirmDelete(introduction) {
      this.introductionToDelete = introduction;
      this.showDeleteDialog = true;
    },
    async deleteIntroduction() {
      if (!this.introductionToDelete) return;

      this.deleting = true;
      try {
        await introductionApi.deleteIntroduction(this.introductionToDelete.id);
        this.showDeleteDialog = false;
        this.introductionToDelete = null;
        await this.loadIntroductions();
      } catch (e) {
        this.$handleApiError(e);
      }
      this.deleting = false;
    }
  },
  mounted() {
    if (this.site && this.site.id) {
      if (this.$isTenantAdmin || this.$isTenantManager) {
        this.loadIntroductions();
        this.loadWorkers();
      } else {
        this.loadMyIntroduction();
      }
    }
  }
}
</script>

<style scoped>

</style>
